import type {
  CamposDeIntegracao,
  Dre,
  SituacaoDoCredenciado,
} from '@/modulos/operacao/comum/contrato';
import type { TipoDeCredenciado } from '@/nucleo/pessoaNatureza';

/**
 * O credenciado, do lado da tela.
 *
 * Credenciado e a empresa, a pessoa fisica ou a cooperativa COM TERMO DE ADESAO
 * ATIVO — nao o inscrito nem o pre-credenciado. Essa distincao e a razao de a
 * listagem ter os indicadores que tem: quem abre esta tela quer o que esta valendo
 * hoje, e o funil de entrada e outro modulo.
 *
 * Campos em minusculas com sublinhado: sao os nomes das colunas, e e assim que a API
 * responde (mesma decisao de modulos/admin/integracoes/contrato.ts). A API e
 * `bff/v1/operacao/credenciados` (apps/api/src/modulos/credenciados-operacao), e a
 * conversao da resposta para este tipo mora em `dados.ts`.
 */

/**
 * Veiculo reduzido, como ele aparece pendurado no credenciado na listagem.
 *
 * `crm` e a licenca municipal do veiculo; `placa` e a do Detran. Os dois aparecem
 * juntos porque a operacao procura por um ou por outro, dependendo de qual documento
 * tem na mao.
 */
export interface VeiculoDoCredenciado {
  placa: string | null;
  crm: string | null;
  status: string | null;
}

/**
 * Regime tributario declarado pelo credenciado pessoa juridica.
 *
 * So aparece na tela de detalhe (aba "Dados Gerais") — a listagem nao filtra por
 * isso, e por isso nao ha `OPCOES_DE_REGIME` ao lado das outras opcoes de filtro
 * deste arquivo.
 */
export const REGIMES_TRIBUTARIOS = [
  'SIMPLES',
  'LUCRO_PRESUMIDO',
  'LUCRO_REAL',
  'MEI',
  'ISENTO',
] as const;

export type RegimeTributario = (typeof REGIMES_TRIBUTARIOS)[number];

export const ROTULO_DO_REGIME_TRIBUTARIO: Record<RegimeTributario, string> = {
  SIMPLES: 'Simples Nacional',
  LUCRO_PRESUMIDO: 'Lucro presumido',
  LUCRO_REAL: 'Lucro real',
  MEI: 'MEI',
  ISENTO: 'Isento',
};

export interface Credenciado extends CamposDeIntegracao {
  id: string;
  /**
   * Identificador legivel usado no endereco do detalhe. Nao e o `id`: endereco com
   * UUID nao se le por telefone nem se reconhece num favorito.
   */
  slug: string;
  tipo: TipoDeCredenciado;
  status: SituacaoDoCredenciado;
  /**
   * CPF ou CNPJ JA MASCARADO pela API (so os dois ultimos digitos a vista). O
   * documento inteiro nao sai do banco nem na lista nem no detalhe (CLAUDE.md, regras
   * de API 5 a 7): a tela mostra como veio, sem aplicar mascara de novo.
   */
  cpf_cnpj: string;
  nome: string;
  nome_fantasia: string | null;
  /**
   * E-mail e telefone: MASCARADOS na lista e na exportacao (`f***@dominio`,
   * `(**) ****-1234`); INTEIROS no detalhe (`useCredenciado`), que e onde a aba Dados
   * Gerais os mostra e onde a leitura fica registrada em `acessos_dado_pessoal`.
   */
  email: string | null;
  telefone: string | null;
  /** Segundo telefone de contato. So vem no detalhe; na lista, nulo. */
  telefone2: string | null;
  /** Numero do Termo de Permissao de Uso concedido pela SMT, anterior ao termo de adesao do TEG. */
  termo_permissionario: string | null;
  /** Endereco, contato e conta: so vem no detalhe; na lista, nulos. */
  endereco: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  cep: string | null;
  observacoes: string | null;
  /** Dados bancarios para pagamento das medicoes — so aparecem na tela de detalhe. */
  conta_banco: string | null;
  conta_agencia: string | null;
  conta_numero: string | null;
  conta_operacao: string | null;
  regime_tributario: RegimeTributario | null;
  /**
   * Os marcos do ciclo de credenciamento, para o widget "Ciclo de Credenciamento" da
   * tela de detalhe. HOJE SEMPRE NULOS: nao sao colunas de `credenciados` — vivem na
   * inscricao, no pre-credenciamento, no Termo e na penalidade, e ler cada um e
   * escolha de modelo que ficou fora do lote que ligou esta tela a API. O widget mostra
   * o marco sem data, em vez de uma data que ninguem apurou.
   */
  inscricao_enviada_em: string | null;
  pre_credenciado_em: string | null;
  credenciado_em: string | null;
  suspenso_em: string | null;
  motivo_suspensao: string | null;
  dre_id: string | null;
  dres: Pick<Dre, 'codigo' | 'nome'> | null;
  /** CRM e placa dos veiculos ATIVOS, pela politica de veiculos da 0012. */
  veiculos: VeiculoDoCredenciado[] | null;
  created_at: string;
  updated_at: string;
}

export const OPCOES_DE_TIPO: readonly { value: TipoDeCredenciado; label: string }[] = [
  { value: 'PF', label: 'Pessoa física' },
  { value: 'PJ', label: 'Pessoa jurídica' },
  { value: 'COOPERATIVA', label: 'Cooperativa' },
];

/**
 * Filtro da listagem. A paginacao e sempre do servidor.
 *
 * NAO HA FILTRO DE PENDENCIA FINANCEIRA: a pendencia e juizo derivado das cobrancas
 * administrativas, que ninguem apurou ainda — mesma saida da coluna `Saude` da
 * Parametrizacao (CLAUDE.md, regra 7 de interface). A coluna fica, vazia.
 */
export interface FiltroDeCredenciados {
  pagina: number;
  tamanho: number;
  busca?: string;
  situacao?: string;
  tipo?: string;
  dreIds?: string[];
}

/**
 * Indicadores do credenciamento vigente.
 *
 * Sao TRES numeros, e cada um responde a uma pergunta que a tabela nao responde de
 * relance: quantos estao valendo, quantos entraram no ultimo mes e quantos estao
 * travados. Numero em tela de sistema publico vem de dado real ou nao vem
 * (CLAUDE.md, "Proibido"): sem resposta da API, os cartoes nao aparecem.
 */
export interface IndicadoresDeCredenciados {
  ativos: number;
  novos_30d: number;
  suspensos_ou_bloqueados: number;
}

/**
 * As acoes que a barra de selecao aplica a varios credenciados de uma vez.
 *
 * Sao TODAS mudancas de situacao cadastral, e nenhuma delas e trivial: suspender tira o
 * credenciado das ordens novas, bloquear impede pagamento, descredenciar encerra o
 * vinculo. Por isso cada uma passa por confirmacao — e as tres piores exigem parecer
 * escrito, que e o que sobra na auditoria quando alguem perguntar por que.
 */
export const ACOES_EM_LOTE = ['SUSPENDER', 'REATIVAR', 'BLOQUEAR', 'DESCREDENCIAR'] as const;
export type AcaoEmLote = (typeof ACOES_EM_LOTE)[number];

/** Acao que nao se registra sem justificativa escrita. Reativar nao precisa: ela devolve o normal. */
export const ACOES_QUE_EXIGEM_PARECER: readonly AcaoEmLote[] = [
  'SUSPENDER',
  'BLOQUEAR',
  'DESCREDENCIAR',
];

/**
 * Acao que nao se desfaz clicando de novo — e por isso pede a palavra de confirmacao
 * digitada, e nao so um "tem certeza?" que se aceita no piloto automatico.
 */
export const ACOES_IRREVERSIVEIS: readonly AcaoEmLote[] = ['DESCREDENCIAR'];

export const ROTULO_DA_ACAO: Record<AcaoEmLote, string> = {
  SUSPENDER: 'Suspender',
  REATIVAR: 'Reativar',
  BLOQUEAR: 'Bloquear',
  DESCREDENCIAR: 'Descredenciar',
};

/**
 * Teto de itens por lote.
 *
 * Nao e limite tecnico: e freio. "Selecionar todos desta pagina" duas ou tres vezes
 * acumula rapido, e uma suspensao acidental de duzentos credenciados e um incidente,
 * nao um erro de digitacao. Acima do teto os botoes ficam indisponiveis com o motivo
 * dito na tela, em vez de a acao sair e depois alguem tentar desfazer.
 */
export const MAXIMO_DE_ITENS_EM_LOTE = 100;

/** A palavra que libera acao irreversivel. Maiuscula, e conferida sem diferenciar caixa. */
export const PALAVRA_DE_CONFIRMACAO = 'CONFIRMAR';

/*
 * A conversao de situacao para selo (`situacaoComoSelo`) vive em
 * `modulos/operacao/comum/contrato.ts`: as cinco listagens precisam dela, e uma copia
 * por tela e a forma de um valor novo do banco virar selo cinza em quatro telas e
 * colorido na quinta.
 */
